import {
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { DetailPage } from "../../templates/Page/Detail";
import { DetailPageSkeleton } from "../Skeleton/Detail";

const DEFAULT_DETAIL_PLACEHOLDER = (
  <DetailPage>
    <DetailPageSkeleton />
  </DetailPage>
);

/**
 * React Context that exposes master‑level state (main info, loading flags,
 * etc.) for a given detail page. It is intentionally created without a
 * default value so that consumer hooks can fail fast when the provider is
 * missing.
 */
const DetailPageContext = createContext();

/**
 * DetailPageProvider
 *
 * Provides header‑level state (master record) for a detail screen and exposes
 * loading flags so nested components can react accordingly.
 *
 * Params
 * - children (ReactNode): any react elements rendered inside provider
 * - componentId (string)   : unique identifier for this detail screen
 * - master (object)   : optional pre‑fetched header record
 * - paramId (string|number): id pulled from URL params
 * - getDetail (Function) : RTK‑Query hook produced by createApi
 * - resetDetail (Function) : redux action creator to clear header slice
 * - fallbackUrl (string)   : redirect target when id is missing or error
 * - placeholder (boolean|ReactNode): skeleton while the header query
 *   resolves. ``true`` (default) uses the shared DetailPageSkeleton so
 *   every detail module gets the same loading layout. Pass a custom
 *   node to override, or ``false`` to skip.
 *
 * Returns
 * - ReactElement: provider + children once ready, or redirect / placeholder
 */
export const DetailPageProvider = ({
  children,
  componentId,
  master = {},
  paramId,
  getDetail,
  resetDetail,
  fallbackUrl = "/",
  placeholder = true,
  ...props
}) => {
  /* ---------- Hooks; Query, Dispatch ---------- */
  const {
    isLoading = false,
    isFetching = false,
    isError = false,
    isSuccess = true,
    refetch = () => Promise.resolve(),
  } = typeof getDetail === "function"
    ? getDetail(paramId, {
        refetchOnMountOrArgChange: true,
        skip: !paramId,
      })
    : {};

  const [currentAction, setCurrentAction] = useState(null);

  const dispatch = useDispatch();

  const clearCurrentAction = useCallback(() => setCurrentAction(null), []);

  // Decide whether to show children or loader
  const resolvedPlaceholder = isValidElement(placeholder)
    ? placeholder
    : placeholder
      ? DEFAULT_DETAIL_PLACEHOLDER
      : null;
  const shouldShowChildren = isSuccess && (!resolvedPlaceholder || !isLoading);

  /* ---------- Effect---------- */
  useEffect(() => {
    return () => {
      if (resetDetail) {
        dispatch(resetDetail());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, resetDetail]);

  const contextValue = useMemo(
    () => ({
      clearCurrentAction,
      componentId,
      currentAction,
      isFetching,
      isLoading,
      master,
      paramId,
      refetch,
      setCurrentAction,
      ...props,
    }),
    [
      clearCurrentAction,
      componentId,
      currentAction,
      isFetching,
      isLoading,
      master,
      paramId,
      props,
      refetch,
      setCurrentAction,
    ],
  );

  // Redirect if id missing / backend error
  if (!paramId || isError) return <Navigate to={fallbackUrl} replace />;

  if (!shouldShowChildren && resolvedPlaceholder) {
    return resolvedPlaceholder;
  }

  return shouldShowChildren ? (
    <DetailPageContext.Provider value={contextValue}>{children}</DetailPageContext.Provider>
  ) : null;
};

/**
 * Hook to consume the header‑level context. Throws a descriptive error when
 * used outside the provider so integration mistakes are surfaced early.
 *
 * Returns
 * - object: value provided by <DetailPageProvider>
 */
export const useDetailPageContext = () => {
  const ctx = useContext(DetailPageContext);
  if (!ctx) {
    throw new Error("useDetailPageContext must be used inside <DetailPageProvider>");
  }
  return ctx;
};
